import { create } from "zustand";
import { createJSONStorage, persist, StateStorage } from "zustand/middleware";
import { Preferences } from "@capacitor/preferences";
import { Capacitor } from "@capacitor/core";
import * as Types from "@/models/types";
import * as serverClient from "@/models/serverClient";
import { useAuth } from "@/models/useAuth";
import { useUISlice } from "./useUISlice";
import { i18n } from "@/locales";
import { Clipboard } from "@capacitor/clipboard";
import removeMd from "remove-markdown";
import toast from "react-hot-toast";
import {
  downloadFile,
  downloadMarkdownAsDocx,
  downloadTranscriptAsDocx,
  downloadTranscriptAsPdf,
  formatDuration,
  getBasePath,
} from "./utils";
import { useRouter } from "@/_router";
import { Purchases, PurchasesPackage } from "@revenuecat/purchases-capacitor";
import { isNativeApp } from "@/models/platform";
import { logFunnelEvent } from "@/fetchData/datadog";
import AlertWidgetPaywall from "@/ui/AlertWidgetPaywall";

let _toastId: string | undefined;
let _statuses: { [key: string]: string } = {};
const storage: StateStorage = {
  getItem: async (name: string): Promise<string | null> => {
    const { value } = await Preferences.get({ key: name });
    return value;
  },
  setItem: async (name: string, value: string): Promise<void> => {
    await Preferences.set({ key: name, value });
  },
  removeItem: async (name: string): Promise<void> => {
    await Preferences.remove({ key: name });
  },
};

type State = {
  _hasHydrated: boolean;
  userToken?: string;
  isSubscribed: boolean;
  userData?: Partial<Types.User>;
  aiTemplates: Types.AITemplate[];
  notes: Types.Note[];
  folders: Types.Folder[];
};

type Actions = {
  setUserToken: (token: string) => void;
  setHasHydrated: (status: boolean) => void;
  setIsSubscribed: (isSubscribed: boolean) => void;
  subscription: () => {
    status: () => Promise<void>;
    stripeCheckout: (plan: Types.SubscriptionPlan) => Promise<void>;
    revenueCatPurchase: (pkg: PurchasesPackage) => Promise<void>;
    onStripeCheckoutSuccess: (params: {
      router: ReturnType<typeof useRouter>;
    }) => Promise<void>;
    revenueCatRestorePurchase: () => Promise<void>;
    stripeBillingPortal: () => Promise<void>;
  };
  user: () => {
    get: () => Promise<void>;
    saveTemplate: (
      template: Types.User["listTemplates"][number]
    ) => Promise<void>;
    deleteTemplate: (
      templateId: string,
      callback?: () => void
    ) => Promise<void>;
    update: (updates: Partial<Types.User>) => Promise<void>;
    getAITemplates: () => Promise<void>;
    profile: (
      onNoteListCallback?: (nextToken: string) => void
    ) => Promise<void>;
  };
  note: () => {
    create: (
      payload: Pick<
        Types.Note,
        | "type"
        | "fileUrl"
        | "transcriptionLanguage"
        | "status"
        | "name"
        | "transcript"
      >,
      folderId?: string,
      callback?: (note: Types.Note) => void
    ) => Promise<void>;
    liveRecordingChunk: (params: {
      noteId: string;
      action: "setFormat" | "addChunk" | "finalize" | "status";
      format?: Types.LiveRecordingChunk["format"];
      chunk?: Types.LiveRecordingChunk["chunk"];
      callback?: (status: Types.LiveRecordingStatus) => void;
    }) => Promise<void>;
    update: (
      noteId: string,
      updates: Partial<Types.Note>,
      callback?: (note: Types.Note) => void,
      disableOptimisticUpdate?: boolean
    ) => Promise<void>;
    onLiveRecordingCompleted: (noteId: string) => Promise<void>;
    onUploadCompleted: (noteId: string, downloadUrl: string) => Promise<void>;
    transcribeWithSpeakerLabels: (noteId: string) => Promise<void>;
    startTranscription: (noteId: string) => Promise<void>;
    renameSpeaker: (
      noteId: string,
      segment: Types.Note["transcript"][number]
    ) => Promise<void>;
    rename: (noteId: string) => Promise<void>;
    status: (noteId: string) => Promise<void>;
    exportTranscript: (noteId: string) => Promise<void>;
    sendMessage: (noteId: string, message: string) => Promise<void>;
    copyChat: (noteId: string) => Promise<void>;
    clearChat: (noteId: string) => Promise<void>;
    exportAIContent: (
      noteId: string,
      markdown: string,
      type: "pdf" | "text" | "markdown" | "docx"
    ) => Promise<void>;
    copyAIContent: (
      noteId: string,
      id: Types.Note["listAIContent"][number]["id"]
    ) => Promise<void>;
    emailNote: (noteId: string) => Promise<void>;
    copyTranscript: (noteId: string) => Promise<void>;
    emailTranscript: (noteId: string) => Promise<void>;
    delete: (
      noteId: string,
      callback?: () => void,
      skipAlert?: boolean
    ) => Promise<void>;
    deleteAIContent: (
      noteId: string,
      id: Types.Note["listAIContent"][number]["id"],
      callback?: () => void
    ) => Promise<void>;
    generateAIContent: (
      noteId: string,
      type: Types.Note["listAIContent"][number]["type"],
      userTemplateId?: string,
      forceRegenerate?: boolean
    ) => Promise<void>;
    editWithAI: (
      noteId: string,
      userTemplateId: string,
      prompt: string,
      saveUpdates: boolean
    ) => Promise<void>;
    editAIContent: (
      noteId: string,
      contentId: string,
      updates: Partial<Types.Note["listAIContent"][number]>
    ) => Promise<void>;
    editTranscript: (
      noteId: string,
      segment: Types.Note["transcript"][number]
    ) => Promise<void>;
    list: (
      nextToken?: string,
      callback?: (nextToken: string) => void
    ) => Promise<void>;
  };
  folder: () => {
    create: () => Promise<void>;
    update: (folderId: string, updates: Partial<Types.Folder>) => Promise<void>;
    rename: (folderId: string) => Promise<void>;
    addNote: (folderId: string, noteId: string) => Promise<void>;
    removeNote: (folderId: string, noteId: string) => Promise<void>;
    sendMessage: (folderId: string, message: string) => Promise<void>;
    copyChat: (folderId: string) => Promise<void>;
    clearChat: (folderId: string) => Promise<void>;
    delete: (folderId: string) => Promise<void>;
    list: () => Promise<void>;
    listNotes: (folderId: string) => Promise<Types.Note[]>;
  };

  reset: () => void;
};

export const initialState: State = {
  _hasHydrated: false,
  isSubscribed: false,
  notes: [],
  folders: [],
  aiTemplates: [],
};

// create store
export const useUserSlice = create<State & Actions>()(
  persist(
    (set, get) => ({
      ...initialState,
      setHasHydrated: (status) => {
        set({ _hasHydrated: status });
      },
      setUserToken: (token) => {
        set({ userToken: token });
      },
      setIsSubscribed: (isSubscribed) => {
        set({ isSubscribed });
      },
      user: () => ({
        get: async () => {
          if (!useAuth.isAuthenticated()) return;

          const user = await serverClient.userAsync({ type: "get" });
          if (!user?.data?.user) return;

          set({ userData: user.data.user });
        },
        update: async (updates) => {
          if (!useAuth.isAuthenticated()) return;

          set({
            userData: { ...get().userData, ...updates },
          });

          const res = await serverClient.userAsync({
            type: "update",
            payload: { updates },
          });

          if (!res?.data?.user) return;

          set({
            userData: res.data.user,
          });
        },
        getAITemplates: async () => {
          if (!useAuth.isAuthenticated()) return;

          const res = await serverClient.userAsync({ type: "getAITemplates" });
          if (!res?.data?.aiTemplates) return;

          set({ aiTemplates: res.data.aiTemplates });
        },
        saveTemplate: async (template) => {
          if (!useAuth.isAuthenticated()) return;

          const previousTemplates = get().userData?.listTemplates || [];
          const existingTemplateIndex = previousTemplates.findIndex(
            (t) => t.id === template.id
          );

          if (existingTemplateIndex !== -1) {
            previousTemplates[existingTemplateIndex] = template;
          } else {
            previousTemplates.push(template);
          }

          set({
            userData: { ...get().userData, listTemplates: previousTemplates },
          });

          await serverClient.userAsync({
            type: "update",
            payload: { updates: { listTemplates: previousTemplates } },
          });

          const userRes = await serverClient.userAsync({ type: "get" });
          if (!userRes?.data?.user) return;

          set({ userData: userRes.data.user });
          toast.success(i18n.t("useUserSlice.saveTemplateSuccess"));
        },
        deleteTemplate: async (templateId, callback) => {
          if (!useAuth.isAuthenticated()) return;

          const handleDelete = async () => {
            const previousTemplates = get().userData?.listTemplates || [];
            const newTemplates = previousTemplates.filter(
              (t) => t.id !== templateId
            );

            set({
              userData: { ...get().userData, listTemplates: newTemplates },
            });

            await serverClient.userAsync({
              type: "update",
              payload: { updates: { listTemplates: newTemplates } },
            });

            const userRes = await serverClient.userAsync({ type: "get" });
            if (!userRes?.data?.user) return;

            set({ userData: userRes.data.user });
            toast.success(i18n.t("useUserSlice.deleteTemplateSuccess"));
          };

          useUISlice.getState().setAlertModal({
            title: i18n.t("useUserSlice.deleteTemplateTitle"),
            subtitle: i18n.t("useUserSlice.deleteTemplateSubtitle"),
            cancelText: i18n.t("useUserSlice.cancelButton"),
            onDismiss: () => {
              useUISlice.getState().setAlertModal(undefined);
              useUISlice.getState().setAlertWidget("createAIContent");
            },
            button: {
              text: i18n.t("useUserSlice.deleteTemplateButton"),
              destructive: true,
              onPress: () => {
                handleDelete();
                useUISlice.getState().setAlertModal(undefined);
                useUISlice.getState().setAlertWidget("createAIContent");
              },
            },
          });

          if (callback) {
            callback();
          }
        },
        profile: async (onNoteListCallback) => {
          if (!useAuth.isAuthenticated()) return;
          await Promise.all([
            get().note().list(undefined, onNoteListCallback),
            get().folder().list(),
            get().user().get(),
            get().user().getAITemplates(),
          ]);
        },
      }),
      note: () => ({
        create: async (payload, folderId, callback) => {
          if (!useAuth.isAuthenticated()) return;

          const res = await serverClient.noteAsync({ type: "create", payload });

          if (res?.error && res?.error === "SubscriptionRequired") {
            useUISlice.getState().setAlertWidget(undefined);
            useUISlice.getState().setPaywallVisible(true);
            return;
          }

          if (!res?.data?.note) return;

          logFunnelEvent("[3] - note created");

          if (folderId) {
            get().folder().addNote(folderId, res.data.note.id);
          }

          useUISlice.getState().setSelectedFolder(undefined);
          set({ notes: [res.data.note, ...get().notes] });

          if (callback) {
            callback(res.data.note);
          }
        },
        liveRecordingChunk: async (params) => {
          if (!useAuth.isAuthenticated()) return;

          const res = await serverClient.noteAsync({
            type: "liveRecordingChunk",
            payload: {
              noteId: params.noteId,
              action: params.action,
              format: params.format,
              chunk: params.chunk,
            },
          });

          if (res?.data?.liveRecordingStatus && params.callback) {
            params.callback(res.data.liveRecordingStatus);
          }
        },
        transcribeWithSpeakerLabels: async (noteId) => {
          if (!useAuth.isAuthenticated()) return;

          const note = get().notes.find((note) => note.id === noteId);
          if (!note || note.transcriptionService === "assemblyai") return;

          const transcribeRes = await serverClient.transcribeAsync({
            noteId,
            type: "Media",
            withSpeakerLabels: true,
          });

          if (!transcribeRes?.data?.note) return;

          logFunnelEvent("[3.1] - note transcribed with speaker labels");

          get().note().update(noteId, transcribeRes.data.note);

          if (transcribeRes?.data?.note?.status === "transcription_started") {
            get().note().status(noteId);
          }
        },
        startTranscription: async (noteId) => {
          if (!useAuth.isAuthenticated()) return;

          const note = get().notes.find((note) => note.id === noteId);

          const toTranscribeType: {
            [key in Types.Note["type"]]: Types.ApiRequestTranscribe["payload"]["type"];
          } = {
            ocr_text: "OCR",
            pdf_import: "PDF",
            voice_recording: "Media",
            media_import: "Media",
            text: "Media",
          };

          const transcribeRes = await serverClient.transcribeAsync({
            noteId,
            type: toTranscribeType[note?.type],
          });

          if (!transcribeRes?.data?.note) {
            useUserSlice.getState().note().update(noteId, {
              status: "transcription_failed",
            });
            return;
          }

          get().note().update(noteId, transcribeRes.data.note);

          if (transcribeRes?.data?.note?.status === "transcription_started") {
            get().note().status(noteId);
          }
        },
        renameSpeaker: async (noteId, segment) => {
          if (!useAuth.isAuthenticated()) return;

          const note = get().notes.find((note) => note.id === noteId);
          if (!note) return;

          useUISlice.getState().setAlertPrompt({
            title: i18n.t("useUserSlice.renameSpeaker"),
            placeholder: "",
            defaultValue: isNaN(Number(segment.speaker)) ? segment.speaker : "",
            button: {
              text: i18n.t("useUserSlice.save"),
              onPress: (text: string) => {
                get()
                  .note()
                  .update(noteId, {
                    transcript: note.transcript.map((s) =>
                      s.speaker === segment.speaker
                        ? { ...s, speaker: text.trim() }
                        : s
                    ),
                  });
                useUISlice.getState().setAlertPrompt(undefined);
              },
            },
          });
        },
        update: async (noteId, updates, callback, disableOptimisticUpdate) => {
          if (!useAuth.isAuthenticated()) return;

          if (!get().notes.find((note) => note.id === noteId)) return;

          if (!disableOptimisticUpdate) {
            set({
              notes: get().notes.map((note) =>
                note.id === noteId ? { ...note, ...updates } : note
              ),
            });
          }

          const res = await serverClient.noteAsync({
            type: "update",
            payload: { id: noteId, updates },
          });

          if (res?.data?.note) {
            if (callback) {
              callback(res.data.note);
            }
            set({
              notes: get().notes.map((note) =>
                note.id === noteId ? res.data.note : note
              ),
            });
          }
        },
        status: async (noteId) => {
          if (!useAuth.isAuthenticated()) return;

          const note = get().notes.find((note) => note.id === noteId);
          if (!note) return;
          if (_statuses[noteId]) return;

          let iteration = 1;
          _statuses[noteId] = note.status;
          const intervalId = setInterval(async () => {
            iteration += 1;

            if (iteration >= 120) {
              clearInterval(intervalId);
              return;
            }

            const getNoteResponse = await serverClient.noteAsync({
              type: "get",
              payload: { id: noteId },
            });
            const note = getNoteResponse.data.note;

            if (!note) {
              clearInterval(intervalId);
              return;
            }

            set({
              notes: get().notes.map((n) => (n.id === noteId ? note : n)),
            });

            if (note.status === "summary_completed") {
              clearInterval(intervalId);
              return;
            }
          }, 2000);
        },
        onLiveRecordingCompleted: async (noteId) => {
          if (!useAuth.isAuthenticated()) return;

          const note = get().notes.find((note) => note.id === noteId);
          if (!note) return;

          if (note.isProcessing) return;

          set({
            notes: get().notes.map((note) =>
              note.id === noteId ? { ...note, isProcessing: true } : note
            ),
          });

          await serverClient.noteAsync({
            type: "onLiveRecordingCompleted",
            payload: { noteId },
          });

          get().note().status(noteId);
        },
        onUploadCompleted: async (noteId, downloadUrl) => {
          if (!useAuth.isAuthenticated()) return;

          const note = get().notes.find((note) => note.id === noteId);
          if (!note) return;

          set({
            notes: get().notes.map((n) =>
              n.id === noteId ? { ...n, isProcessing: true } : n
            ),
          });

          if (note.isProcessing) return;

          await serverClient.noteAsync({
            type: "onUploadCompleted",
            payload: { noteId, downloadUrl },
          });

          get().note().status(noteId);
        },
        list: async (nextToken, callback) => {
          if (!useAuth.isAuthenticated()) return;

          const notes = await serverClient.noteAsync({
            type: "list",
            payload: { nextToken },
          });
          if (!notes?.data?.notes) return;

          set({
            notes: nextToken
              ? [...get().notes, ...notes.data.notes]
              : notes.data.notes,
          });

          if (callback) {
            callback(notes.data.nextToken);
          }
        },
        delete: async (noteId, callback, skipAlert) => {
          if (!useAuth.isAuthenticated()) return;

          const deleteNote = () => {
            const noteFolder = get().folders.find((folder) =>
              folder?.listNotesIds?.includes(noteId)
            );

            if (noteFolder) {
              get().folder().removeNote(noteFolder.id, noteId);
            }

            set({
              notes: get().notes.filter((note) => note.id !== noteId),
            });
            toast.success(i18n.t("useUserSlice.deleteNoteSuccess"));
            serverClient.noteAsync({
              type: "delete",
              payload: { id: noteId },
            });
            logFunnelEvent("[3.3] - note deleted");
            callback?.();
            useUISlice.getState().setAlertModal(undefined);
          };

          if (skipAlert) {
            deleteNote();
            return;
          }

          useUISlice.getState().setAlertModal({
            title: i18n.t("useUserSlice.deleteNoteTitle"),
            subtitle: i18n.t("useUserSlice.deleteNoteSubtitle"),
            cancelText: i18n.t("useUserSlice.cancelButton"),
            button: {
              text: i18n.t("useUserSlice.deleteNoteDeleteButton"),
              destructive: true,
              onPress: async () => {
                deleteNote();
              },
            },
          });
        },
        rename: async (noteId) => {
          if (!useAuth.isAuthenticated()) return;

          useUISlice.getState().setAlertPrompt({
            title: i18n.t("useUserSlice.renameNoteTitle"),
            defaultValue: get().notes.find((note) => note.id === noteId)?.name,
            placeholder: i18n.t("useUserSlice.renameNoteSubtitle"),
            button: {
              text: i18n.t("useUserSlice.renameNoteRenameButton"),
              onPress: (text: string) => {
                if (text.trim().length === 0) return;

                useUserSlice
                  .getState()
                  .note()
                  .update(noteId, { name: text.trim() });
                toast.success(i18n.t("useUserSlice.renameNoteSuccess"));
                useUISlice.getState().setAlertPrompt(undefined);
              },
            },
          });
        },
        editTranscript: async (noteId, segment) => {
          if (!useAuth.isAuthenticated()) return;

          const note = get().notes.find((note) => note.id === noteId);
          if (!note) return;

          logFunnelEvent("[4.2] - edit transcript");

          useUISlice.getState().setAlertPrompt({
            title: i18n.t("useUserSlice.editTranscriptTitle"),
            defaultValue: segment.text,
            placeholder: i18n.t("useUserSlice.editTranscriptSubtitle"),
            multiLine: true,
            button: {
              text: i18n.t("useUserSlice.editTranscriptButton"),
              onPress: (text: string) => {
                useUserSlice
                  .getState()
                  .note()
                  .update(noteId, {
                    transcript: note.transcript.map((s) =>
                      s.start === segment.start
                        ? { ...s, text: text.trim() }
                        : s
                    ),
                  });
                toast.success(i18n.t("useUserSlice.editTranscriptSuccess"));
                useUISlice.getState().setAlertPrompt(undefined);
              },
            },
          });
        },
        editWithAI: async (noteId, userTemplateId, prompt, saveUpdates) => {
          if (!useAuth.isAuthenticated()) return;

          const note = get().notes.find((note) => note.id === noteId);
          if (!note) return;

          const res = await serverClient.noteAsync({
            type: "editWithAI",
            payload: { id: noteId, userTemplateId, prompt, saveUpdates },
          });

          if (!res?.data?.note) return;

          get().note().update(noteId, res.data.note);
          get().user().get();
        },
        generateAIContent: async (
          noteId,
          type,
          userTemplateId,
          forceRegenerate
        ) => {
          if (!useAuth.isAuthenticated()) return;

          const note = get().notes.find((note) => note.id === noteId);
          if (!note) return;

          const existingItemByType = note?.listAIContent?.find(
            (content) =>
              content.type !== "custom_summary" && content.type === type
          );

          const existingItemByUserTemplateId = note?.listAIContent?.find(
            (content) =>
              content.type === "custom_summary" && content.id === userTemplateId
          );

          if (
            !forceRegenerate &&
            (existingItemByType || existingItemByUserTemplateId)
          ) {
            return;
          }

          logFunnelEvent("[5] - summary requested");

          const res = await serverClient.noteAsync({
            type: "generateAIContent",
            payload: { id: noteId, type, userTemplateId },
          });

          if (!res?.data?.note) return;

          logFunnelEvent("[5.1] - summary created");

          get().note().update(noteId, res.data.note);

          toast.success(i18n.t("useUserSlice.generateAIContentSuccess"));
        },
        editAIContent: async (noteId, contentId, updates) => {
          if (!useAuth.isAuthenticated()) return;

          const note = get().notes.find((note) => note.id === noteId);
          if (!note) return;

          const previousContent = note.listAIContent.find(
            (content) => content.id === contentId
          );
          if (!previousContent) return;

          logFunnelEvent("[5.2] - summary edited");

          useUserSlice
            .getState()
            .note()
            .update(noteId, {
              listAIContent: note.listAIContent.map((content) =>
                content.id === contentId ? { ...content, ...updates } : content
              ),
            });
        },
        copyTranscript: async (noteId) => {
          if (!useAuth.isAuthenticated()) return;

          const note = get().notes.find((note) => note.id === noteId);
          if (!note) return;

          const text = note.transcript
            ?.map((segment) => segment.text)
            .join("\n\n");

          if (!text) return;

          logFunnelEvent("[4.3] - transcript copied");

          Clipboard.write({ string: text }).then(() => {
            toast.success(i18n.t("useUserSlice.copyTranscriptSuccess"));
          });
        },
        emailTranscript: async (noteId) => {
          if (!useAuth.isAuthenticated()) return;

          const note = get().notes.find((note) => note.id === noteId);
          if (!note) return;

          const text = note.transcript
            ?.map((segment) => segment.text)
            .join("\n\n");

          if (!text) return;

          logFunnelEvent("[4.4] - transcript emailed");

          window.open(
            `mailto:${get().userData?.email}?subject=${encodeURIComponent(note.name)}&body=${encodeURIComponent(text)}`,
            "_self"
          );
        },
        copyAIContent: async (noteId, id) => {
          if (!useAuth.isAuthenticated()) return;

          const note = get().notes.find((note) => note.id === noteId);
          if (!note) return;

          const content = note.listAIContent.find(
            (content) => content.id === id
          );
          if (!content) return;
          const text = removeMd(content.markdown);

          logFunnelEvent("[5.3] - summary copied");

          Clipboard.write({ string: text }).then(() => {
            toast.success(i18n.t("useUserSlice.copied"));
          });
        },
        emailNote: async (noteId) => {
          if (!useAuth.isAuthenticated()) return;

          const note = get().notes.find((note) => note.id === noteId);
          const userTemplates = get().userData?.listTemplates;
          const listAIContent = note?.listAIContent;
          if (!note) return;

          const text = removeMd(
            listAIContent
              ?.map((content) => {
                const userTemplate = userTemplates?.find(
                  (template) => template.id === content.id
                );
                return `${userTemplate?.title || i18n.t("useUserSlice.summary")}\n\n${content.markdown}`;
              })
              .join(`\n\n`)
          );

          logFunnelEvent("[5.4] - summary emailed");

          window.open(
            `mailto:${get().userData?.email}?subject=${encodeURIComponent(note.name)}&body=${encodeURIComponent(text)}`,
            "_self"
          );
        },
        exportAIContent: async (noteId, markdown, type) => {
          if (!useAuth.isAuthenticated()) return;

          const note = get().notes.find((note) => note.id === noteId);
          if (!note) return;

          switch (type) {
            case "pdf":
              _toastId = toast.loading(
                i18n.t("useUserSlice.exportAIContentLoading")
              );
              const { marked } = await import("marked");
              const htmlContent = await marked(markdown);
              const res = await serverClient.noteAsync({
                type: "downloadPDF",
                payload: { id: noteId, htmlString: htmlContent },
              });

              if (res?.data?.pdfBase64) {
                await downloadFile({
                  type: "url",
                  url: `data:application/pdf;base64,${res.data.pdfBase64}`,
                  fileName: `${note.name}.pdf`,
                });
              }

              toast.dismiss(_toastId);
              break;

            case "text":
              downloadFile({
                type: "txt",
                text: removeMd(markdown),
                fileName: `${note.name}.txt`,
              });
              break;

            case "markdown":
              downloadFile({
                type: "txt",
                text: markdown,
                fileName: `${note.name}.md`,
              });
              break;

            case "docx":
              await downloadMarkdownAsDocx({
                markdown,
                fileName: `${note.name}`,
              });
              break;
          }
        },
        exportTranscript: async (noteId) => {
          if (!useAuth.isAuthenticated()) return;

          const note = get().notes.find((note) => note.id === noteId);
          if (!note) return;

          logFunnelEvent("[4.5] - transcript downloaded");

          if (["voice_recording", "media_import"].includes(note.type)) {
            useUISlice.getState().setAlertExportTranscriptOptions({
              hasSpeakers: note.transcript.some((segment) => segment.speaker),
              onExport: async ({
                withSpeakers,
                withTimestamps,
                withMonologue,
                exportType,
              }) => {
                let output: { metadata?: string; text: string }[] = [];
                const transcript = note.transcript;

                const getSpeaker = (speaker: string) => {
                  return isNaN(Number(speaker))
                    ? speaker
                    : `${i18n.t("ui.speaker")} ${Number(speaker) + 1}`;
                };

                if (withSpeakers && withTimestamps) {
                  output = transcript.map((t) => ({
                    metadata: `${getSpeaker(t.speaker)} • ${formatDuration(t.start)} - ${formatDuration(t.end)}`,
                    text: t.text,
                  }));
                }

                if (withSpeakers && !withTimestamps) {
                  output = transcript.map((t) => ({
                    metadata: getSpeaker(t.speaker),
                    text: t.text,
                  }));
                }

                if (!withSpeakers && withTimestamps) {
                  const convertToSubtitleTime = (timeInMs: number): string => {
                    const milliseconds = timeInMs % 1000;
                    const seconds = Math.floor((timeInMs / 1000) % 60);
                    const minutes = Math.floor((timeInMs / (1000 * 60)) % 60);
                    const hours = Math.floor(
                      (timeInMs / (1000 * 60 * 60)) % 24
                    );

                    const formattedMilliseconds = String(milliseconds).padStart(
                      3,
                      "0"
                    );
                    const formattedSeconds = String(seconds).padStart(2, "0");
                    const formattedMinutes = String(minutes).padStart(2, "0");
                    const formattedHours = String(hours).padStart(2, "0");

                    return `${formattedHours}:${formattedMinutes}:${formattedSeconds},${formattedMilliseconds}`;
                  };

                  if (exportType === "srt") {
                    output = transcript.map((t) => ({
                      metadata: `${convertToSubtitleTime(t.start * 1000)} - ${convertToSubtitleTime(t.end * 1000)}`,
                      text: t.text,
                    }));
                  } else {
                    output = transcript.map((t) => ({
                      metadata: `${formatDuration(t.start)} - ${formatDuration(t.end)}`,
                      text: t.text,
                    }));
                  }
                }

                if (!withSpeakers && !withTimestamps) {
                  output = transcript.map((t) => ({ text: t.text }));
                }

                if (withMonologue) {
                  output = [{ text: transcript.map((t) => t.text).join(" ") }];
                }

                switch (exportType) {
                  case "txt":
                  case "srt":
                    await downloadFile({
                      type: "txt",
                      text: output
                        .map(({ metadata, text }) => {
                          let content = "";
                          if (metadata) {
                            content += metadata + "\n";
                          }
                          content += text.trim() + "\n\n";
                          return content;
                        })
                        .join(""),
                      fileName: `${note.name}.${exportType}`,
                    });
                    break;

                  case "docx":
                    await downloadTranscriptAsDocx({
                      fileName: `${note.name}`,
                      items: output,
                    });
                    break;
                  case "pdf":
                    await downloadTranscriptAsPdf({
                      fileName: `${note.name}`,
                      items: output,
                    });
                    break;
                }
              },
            });
          } else {
            const output: { text: string; metadata?: string }[] =
              note.transcript.map((t) => ({ text: t.text }));

            useUISlice.getState().setAlertSelect({
              title: i18n.t("useUserSlice.exportAIContentTitle"),
              options: [
                i18n.t("useUserSlice.exportAIContentOptionPdf"),
                i18n.t("useUserSlice.exportAIContentOptionDocx"),
                i18n.t("useUserSlice.exportAIContentOptionTxt"),
              ],
              onSelect: async (option) => {
                switch (option) {
                  case 0:
                    await downloadTranscriptAsPdf({
                      fileName: `${note.name}`,
                      items: output,
                    });
                    break;

                  case 1:
                    await downloadTranscriptAsDocx({
                      fileName: `${note.name}`,
                      items: output,
                    });
                    break;

                  case 2:
                    await downloadFile({
                      type: "txt",
                      text: output.map(({ text }) => text).join("\n\n"),
                      fileName: `${note.name}.txt`,
                    });
                    break;
                }
              },
            });
          }
        },
        deleteAIContent: async (noteId, id, callback) => {
          if (!useAuth.isAuthenticated()) return;

          logFunnelEvent("[5.6] - summary deleted");

          useUISlice.getState().setAlertModal({
            title: i18n.t("useUserSlice.deleteAIContentTitle"),
            subtitle: i18n.t("useUserSlice.deleteAIContentSubtitle"),
            cancelText: i18n.t("useUserSlice.cancelButton"),
            button: {
              text: i18n.t("useUserSlice.deleteAIContentButton"),
              destructive: true,
              onPress: async () => {
                const note = get().notes.find((note) => note.id === noteId);
                if (!note) return;

                useUserSlice
                  .getState()
                  .note()
                  .update(noteId, {
                    listAIContent: note.listAIContent.filter(
                      (content) => content.id !== id
                    ),
                  });

                callback?.();
                toast.success(i18n.t("useUserSlice.deleted"));
                useUISlice.getState().setAlertModal(undefined);
              },
            },
          });
        },
        sendMessage: async (noteId, message) => {
          if (!useAuth.isAuthenticated()) return;

          const note = get().notes.find((note) => note.id === noteId);
          if (!note) return;

          let chat = note.chat;
          if (!chat) {
            chat = {
              messages: [],
              createdAt: new Date().toISOString(),
            };
          }

          chat.messages.push({
            role: "user",
            content: message,
            createdAt: new Date().toISOString(),
          });

          chat.messages.push({
            role: "assistant",
            content: i18n.t("useUserSlice.sendMessageLoading"),
            createdAt: new Date().toISOString(),
          });

          set({
            notes: get().notes.map((note) =>
              note.id === noteId ? { ...note, chat } : note
            ),
          });

          const res = await serverClient.noteAsync({
            type: "sendChatMessage",
            payload: { id: noteId, content: message },
          });

          if (!res?.data?.note) return;

          logFunnelEvent("[6] - chat message sent");

          get().note().update(noteId, { chat: res.data.note.chat });
        },
        copyChat: async (noteId) => {
          if (!useAuth.isAuthenticated()) return;

          const note = get().notes.find((note) => note.id === noteId);
          if (!note) return;

          logFunnelEvent("[6.1] - chat copied");

          Clipboard.write({
            string: note.chat?.messages.map((m) => m.content).join("\n") ?? "",
          }).then(() => {
            toast.success(i18n.t("useUserSlice.copied"));
          });
        },
        clearChat: async (noteId) => {
          if (!useAuth.isAuthenticated()) return;

          const note = get().notes.find((note) => note.id === noteId);
          if (!note) return;

          logFunnelEvent("[6.2] - chat cleared");

          useUISlice.getState().setAlertModal({
            title: i18n.t("useUserSlice.clearChatTitle"),
            subtitle: i18n.t("useUserSlice.clearChatSubtitle"),
            cancelText: i18n.t("useUserSlice.cancelButton"),
            button: {
              text: i18n.t("useUserSlice.clearChatButton"),
              destructive: true,
              onPress: async () => {
                useUserSlice
                  .getState()
                  .note()
                  .update(noteId, {
                    chat: {
                      messages: [],
                      createdAt: new Date().toISOString(),
                    },
                  });

                toast.success(i18n.t("useUserSlice.clearChatSuccess"));
                useUISlice.getState().setAlertModal(undefined);
              },
            },
          });
        },
      }),
      folder: () => ({
        create: async () => {
          if (!useAuth.isAuthenticated()) return;

          useUISlice.getState().setAlertPrompt({
            title: i18n.t("useUserSlice.createFolderTitle"),
            placeholder: i18n.t("useUserSlice.createFolderSubtitle"),
            button: {
              text: i18n.t("useUserSlice.createFolderButton"),
              onPress: async (text: string) => {
                if (text.trim().length === 0) return;

                if (
                  get().folders.find(
                    (folder) =>
                      folder.name.trim().toLowerCase() ===
                      text.trim().toLowerCase()
                  )
                ) {
                  toast.error(i18n.t("useUserSlice.createFolderErrorName"));
                  return;
                }

                useUISlice.getState().setAlertPrompt(undefined);

                logFunnelEvent("[7] - folder created");

                const res = await serverClient.folderAsync({
                  type: "create",
                  payload: { name: text.trim() },
                });

                if (!res?.data?.folder) return;

                set({ folders: [...get().folders, res.data.folder] });
                toast.success(i18n.t("useUserSlice.createFolderSuccess"));
              },
            },
          });
        },
        update: async (folderId, updates) => {
          if (!useAuth.isAuthenticated()) return;

          if (!get().folders.find((folder) => folder.id === folderId)) return;

          set({
            folders: get().folders.map((folder) =>
              folder.id === folderId ? { ...folder, ...updates } : folder
            ),
          });

          const res = await serverClient.folderAsync({
            type: "update",
            payload: { id: folderId, updates },
          });

          if (!res?.data?.folder) return;

          set({
            folders: get().folders.map((folder) =>
              folder.id === folderId ? res.data.folder : folder
            ),
          });
        },
        list: async () => {
          if (!useAuth.isAuthenticated()) return;

          const folders = await serverClient.folderAsync({ type: "list" });
          if (!folders?.data?.folders) return;

          set({ folders: folders.data.folders });
        },
        delete: async (folderId) => {
          if (!useAuth.isAuthenticated()) return;

          useUISlice.getState().setAlertModal({
            title: i18n.t("useUserSlice.deleteFolderTitle"),
            subtitle: i18n.t("useUserSlice.deleteFolderSubtitle"),
            cancelText: i18n.t("useUserSlice.cancelButton"),
            button: {
              text: i18n.t("useUserSlice.deleteFolderDeleteButton"),
              destructive: true,
              onPress: async () => {
                set({
                  folders: get().folders.filter(
                    (folder) => folder.id !== folderId
                  ),
                });
                toast.success(i18n.t("useUserSlice.deleteFolderSuccess"));
                serverClient.folderAsync({
                  type: "delete",
                  payload: { id: folderId },
                });
                logFunnelEvent("[7.2] - folder deleted");
                useUISlice.getState().setAlertModal(undefined);
              },
            },
          });
        },
        rename: async (folderId) => {
          if (!useAuth.isAuthenticated()) return;

          useUISlice.getState().setAlertPrompt({
            title: i18n.t("useUserSlice.renameFolderTitle"),
            defaultValue: get().folders.find((folder) => folder.id === folderId)
              ?.name,
            placeholder: i18n.t("useUserSlice.renameFolderSubtitle"),
            button: {
              text: i18n.t("useUserSlice.renameFolderRenameButton"),
              onPress: (text: string) => {
                if (text.trim().length === 0) return;

                logFunnelEvent("[7.1] - folder renamed");

                useUserSlice
                  .getState()
                  .folder()
                  .update(folderId, { name: text.trim() });
                toast.success(i18n.t("useUserSlice.renameFolderSuccess"));
                useUISlice.getState().setAlertPrompt(undefined);
              },
            },
          });
        },
        listNotes: async (folderId) => {
          if (!useAuth.isAuthenticated()) return;

          const notes = await serverClient.folderAsync({
            type: "listNotes",
            payload: { id: folderId },
          });

          return notes?.data?.notes ?? [];
        },
        addNote: async (folderId, noteId) => {
          if (!useAuth.isAuthenticated()) return;
          if (!folderId || !noteId) return;

          const previousFolder = get().folders.find((folder) =>
            folder?.listNotesIds?.includes(noteId)
          );

          if (previousFolder?.id === folderId) return;

          if (previousFolder) {
            get().folder().removeNote(previousFolder.id, noteId);
          }

          logFunnelEvent("[7.3] - add note to folder");

          get()
            .folder()
            .update(folderId, {
              listNotesIds: Array.from(
                new Set([
                  noteId,
                  ...(get().folders.find((f) => f.id === folderId)
                    ?.listNotesIds ?? []),
                ])
              ),
            });
        },
        removeNote: async (folderId, noteId) => {
          if (!useAuth.isAuthenticated()) return;

          let previousFolder = get().folders.find(
            (folder) => folder.id === folderId
          );

          if (previousFolder) {
            logFunnelEvent("[7.4] - remove note from folder");

            useUserSlice
              .getState()
              .folder()
              .update(previousFolder.id, {
                listNotesIds: previousFolder.listNotesIds.filter(
                  (id) => id !== noteId
                ),
              });
          }
        },
        sendMessage: async (folderId, message) => {
          if (!useAuth.isAuthenticated()) return;

          const folder = get().folders.find((folder) => folder.id === folderId);
          if (!folder) return;

          let chat = folder.chat;
          if (!chat) {
            chat = {
              messages: [],
              createdAt: new Date().toISOString(),
            };
          }

          chat.messages.push({
            role: "user",
            content: message,
            createdAt: new Date().toISOString(),
          });

          chat.messages.push({
            role: "assistant",
            content: i18n.t("useUserSlice.sendMessageLoading"),
            createdAt: new Date().toISOString(),
          });

          set({
            folders: get().folders.map((folder) =>
              folder.id === folderId ? { ...folder, chat } : folder
            ),
          });

          const res = await serverClient.folderAsync({
            type: "sendChatMessage",
            payload: { id: folderId, content: message },
          });

          logFunnelEvent("[7.5] - chat with folder");

          if (!res?.data?.folder) return;

          get().folder().update(folderId, { chat: res.data.folder.chat });
        },
        copyChat: async (folderId) => {
          if (!useAuth.isAuthenticated()) return;

          const folder = get().folders.find((folder) => folder.id === folderId);
          if (!folder) return;

          logFunnelEvent("[7.6] - copy chat folder");

          Clipboard.write({
            string:
              folder.chat?.messages.map((m) => m.content).join("\n") ?? "",
          }).then(() => {
            toast.success(i18n.t("useUserSlice.copied"));
          });
        },
        clearChat: async (folderId) => {
          if (!useAuth.isAuthenticated()) return;

          const folder = get().folders.find((folder) => folder.id === folderId);
          if (!folder) return;

          useUISlice.getState().setAlertModal({
            title: i18n.t("useUserSlice.clearChatTitle"),
            subtitle: i18n.t("useUserSlice.clearChatSubtitle"),
            cancelText: i18n.t("useUserSlice.cancelButton"),
            button: {
              text: i18n.t("useUserSlice.clearChatButton"),
              destructive: true,
              onPress: async () => {
                useUserSlice
                  .getState()
                  .folder()
                  .update(folderId, {
                    chat: {
                      messages: [],
                      createdAt: new Date().toISOString(),
                    },
                  });
                logFunnelEvent("[7.7] - clear chat folder");

                toast.success(i18n.t("useUserSlice.clearChatSuccess"));
                useUISlice.getState().setAlertModal(undefined);
              },
            },
          });
        },
      }),
      subscription: () => ({
        status: async () => {
          if (isNativeApp()) {
            const isConfiguredResponse = await Purchases.isConfigured();
            if (!isConfiguredResponse.isConfigured) {
              await AlertWidgetPaywall.configureRevenueCat();
            }

            const customerInfoResponse = await Purchases.getCustomerInfo();
            const activeSubscriptions =
              customerInfoResponse?.customerInfo?.activeSubscriptions;

            if (activeSubscriptions?.length > 0) {
              set({ isSubscribed: true });
              return;
            }
          }

          if (!useAuth.isAuthenticated()) return;

          const res = await serverClient.subscriptionAsync({ type: "status" });
          if (!res?.data?.subscription) return;
          set({ isSubscribed: res?.data?.subscription?.isActive });
        },
        stripeCheckout: async (plan) => {
          if (!useAuth.isAuthenticated()) return;

          const res = await serverClient.subscriptionAsync({
            type: "stripeCheckout",
            payload: { baseAppUrl: window.location.href, plan },
          });
          if (!res?.data?.url) return;
          window.open(res?.data?.url, "_self");
        },
        stripeBillingPortal: async () => {
          if (!useAuth.isAuthenticated()) return;

          const response = await serverClient.subscriptionAsync({
            type: "stripeBillingPortal",
            payload: { baseAppUrl: window.location.href },
          });

          if (!response?.data?.url) return;

          logFunnelEvent("[8.1] - stripe billing portal");

          window.open(response?.data?.url, "_self");
        },
        onStripeCheckoutSuccess: async (params) => {
          if (!useAuth.isAuthenticated()) return;
          const slug = params.router.query.checkout_success;
          if (!slug || slug !== "true") return;

          const fbq = await import("@/fetchData/fpixel");
          const gtag = await import("@/fetchData/gtag");

          const planPrices: Record<Types.SubscriptionPlan, number> = {
            YEARLY_MID_TIER: 119.99,
            MONTHLY_MID_TIER: 19.99,
            WEEKLY_MID_TIER: 8.99,
          };
          const value =
            planPrices[params.router.query.plan as Types.SubscriptionPlan] ||
            19.99;

          fbq.event("Subscribe", { currency: "USD", value: value.toString() });
          gtag.conversion({ value, transactionId: String(Date.now()) });
          get().subscription().status();

          logFunnelEvent("[8.2] - stripe checkout success");

          useUISlice.getState().setAlertModal({
            title: i18n.t("useUserSlice.subscriptionSuccess"),
            subtitle: i18n.t("useUserSlice.subscriptionSuccessDescription"),
            hideDismissIcon: true,
            shouldPersistBackdropClick: true,
            button: {
              text: i18n.t("useUserSlice.subscriptionSuccessButton"),
              onPress: async () => {
                // Wait for 2 seconds to ensure backend has processed the subscription
                useUISlice.getState().setAlertModal({
                  ...useUISlice.getState().alertModal,
                  loading: true,
                });

                await new Promise((resolve) => setTimeout(resolve, 2000));
                useUISlice.getState().setAlertModal(undefined);

                params.router.replace(`${getBasePath()}/home`);

                window.location.reload();
              },
            },
          });
        },
        revenueCatPurchase: async (pkg) => {
          if (!useAuth.isAuthenticated()) return;

          try {
            const gtag = await import("@/fetchData/gtag");

            const { customerInfo } = await Purchases.purchasePackage({
              aPackage: pkg,
            });

            if (customerInfo?.activeSubscriptions?.length > 0) {
              set({ isSubscribed: true });

              gtag.conversion({
                value: pkg.product.price.toString(),
                transactionId: String(Date.now()),
              });

              useUISlice.getState().setPaywallVisible(undefined);
              useUISlice.getState().setRevenueCat({
                ...useUISlice.getState()?.revenueCat,
                customerInfo,
              });
              toast.success(i18n.t("useUserSlice.subscriptionSuccess"));
            }
          } catch (error) {}
        },
        revenueCatRestorePurchase: async () => {
          if (!useAuth.isAuthenticated()) return;

          const restorePurchaseResponse = await Purchases.restorePurchases();
          if (
            !restorePurchaseResponse?.customerInfo?.activeSubscriptions ||
            restorePurchaseResponse?.customerInfo?.activeSubscriptions
              ?.length === 0
          ) {
            toast.error(i18n.t("useUserSlice.noSubscriptionText"));
            return;
          }

          const isSubscribed =
            restorePurchaseResponse.customerInfo.activeSubscriptions.length > 0;
          set({ isSubscribed });

          toast.success(i18n.t("useUserSlice.restorePurchaseSuccess"));
        },
      }),

      reset: () => {
        set({
          userToken: undefined,
          isSubscribed: false,
          notes: [],
          folders: [],
          user: undefined,
        });
      },
    }),
    {
      name: `eznoteai-${Capacitor.getPlatform()}-user-storage`,
      storage: createJSONStorage(() => storage),
      onRehydrateStorage: () => (state) => {
        state && state.setHasHydrated(true);
      },
    }
  )
);
